import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useServerPage } from '../hooks/useServerPage';
import { useProveedores } from '../hooks/useProveedores';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { api, extractErrorMessage } from '../api/client';
import { usePdfImport } from '../hooks/usePdfImport';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { VerInstructivo } from '../components/VerInstructivo';
import { MenuImportar } from '../components/MenuImportar';
import { Confirm } from '../components/Confirm';
import { Pagination } from '../components/Pagination';
import { Button } from '../components/Button';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useAvisoImpactoMl } from '../hooks/useAvisoImpactoMl';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';
import { PendientesDeRecibir, type OrdenPendiente } from './remitos/PendientesDeRecibir';
import { hoyAR } from './flujo-caja/formato';
import { GuiaInteractiva } from '../ayuda/GuiaInteractiva';
import { guiaRemitoNuevo, type ContextoRemito } from '../ayuda/guias/remito';
import { diferenciaRemito } from '../lib/diferenciaRemito';
import { agruparItemsConReparto, conSku, expandirLinea, resumenReparto, type VarianteDeSku, type VariantesDeSku } from '../lib/variantes';
import { RepartoVariantes } from '../components/RepartoVariantes';
import { BuscadorSku, OtrosProveedores, ProveedorDelArticulo } from '../components/BuscadorSku';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { resolverCodigosDocumento, useBuscadorCompra, type ArticuloCompra } from '../hooks/useBuscadorCompra';

interface Remito {
  id: number; numeroRemito?: string; ordenCompraId?: number | null;
  /** OC que cubre el remito: la principal y las de sus ítems (un remito puede traer varias OC del mismo proveedor). */
  ordenesCompra?: { id: number; numeroOrden: string | null }[];
  proveedor?: { nombre: string } | null; proveedorId?: number | null;
  /** Sólo en una devolución de cliente (sin proveedor). */
  cliente?: { nombre: string } | null; clienteId?: number | null;
  motivo?: string;
  fecha: string; estado: string;
  wmsStatus?: string | null; fechaEntregaAcordada?: string; notas?: string;
  /** Por qué no entró al depósito (wmsStatus error): el mensaje del ingreso, a veces técnico. */
  wmsError?: string | null;
  /** Borrador (DECISIONES 2026-10-12): se sigue cargando y todavía no sumó al stock; lo confirma un administrador. */
  borrador?: boolean;
  /** Quién lo cargó y quién lo confirmó (usuario), y cuándo. */
  creadoPorId?: number | null; creadoPor?: string | null; confirmadoPor?: string | null; confirmadoEn?: string | null; createdAt?: string;
  items?: {
    descripcion: string; cantidad: string; cantidadOrdenada?: string; diferenciaDetalle?: string | null; ordenCompraId?: number | null;
    sku?: string | null; codigoProveedor?: string | null; ean?: string | null; cantidadRotura?: string; ordenCompraItemId?: number | null;
    /** Variante del SKU a la que se imputa este ítem (un ítem por variante; ver lib/variantes). */
    varianteId?: number | null;
    /** Nombre de esa variante (detalle); null sin reparto. */
    varianteNombre?: string | null;
  }[];
}
/** Ids de las OC del remito (las de remitos viejos: sólo la principal). */
const idsOC = (r: Remito): number[] => (r.ordenesCompra?.length ? r.ordenesCompra.map(o => o.id) : r.ordenCompraId ? [r.ordenCompraId] : []);
/** "#5", "#5 y #6", "#5, #6 y #7". */
const listaOC = (ids: number[]) => ids.map(id => `#${id}`).join(', ').replace(/, ([^,]*)$/, ' y $1');
/** Motivo de un remito sin OC (GET remitos/motivos): consignación y terceros sólo si el backend los habilita. */
interface MotivoRemito { valor: string; etiqueta: string; contraparte: 'proveedor' | 'cliente' }
/** Cliente para una devolución (GET remitos/clientes): pide remitos:ver (no clientes:ver) y no trae datos monetarios. */
interface ClienteRemito { id: number; nombre: string; cuit: string | null }
const MOTIVOS_BASE: MotivoRemito[] = [
  { valor: 'compra_sin_oc', etiqueta: 'Compra sin OC', contraparte: 'proveedor' },
  { valor: 'bonificacion', etiqueta: 'Bonificación o muestra', contraparte: 'proveedor' },
  { valor: 'devolucion_cliente', etiqueta: 'Devolución de cliente', contraparte: 'cliente' },
  { valor: 'otro_ingreso', etiqueta: 'Otro ingreso', contraparte: 'proveedor' },
];
const ETIQUETAS_MOTIVO: Record<string, string> = {
  compra: 'Compra', ...Object.fromEntries(MOTIVOS_BASE.map(m => [m.valor, m.etiqueta])),
  consignacion: 'Consignación', mercaderia_terceros: 'Mercadería de terceros',
};
/** Mercadería ajena: no suma al stock propio (el remito sólo se registra). */
const MOTIVOS_SIN_STOCK_PROPIO = ['consignacion', 'mercaderia_terceros'];
const etiquetaMotivo = (r: Remito) => ETIQUETAS_MOTIVO[r.motivo ?? ''] ?? (r.ordenCompraId ? 'Compra' : 'Compra sin OC');
const contraparte = (r: Remito) => r.proveedor?.nombre ?? r.cliente?.nombre ?? '—';
/** Qué pasó con el stock (Remito.wmsStatus): con OC lo marca la recepción del depósito; sin OC, el alta. */
const ESTADO_STOCK: Record<string, string> = {
  ok: 'Ingresado', ingresado: 'Ingresado', sin_stock: 'Sin stock propio', skipped: 'Sin stock', error: 'Error', syncing: 'Procesando',
};
/** Un borrador se sigue cargando y lo confirma un administrador; un pendiente viejo (anterior al borrador) no es borrador. */
const ETIQUETAS_ESTADO: Record<string, string> = { pendiente: 'Pendiente', conciliado: 'Confirmado' };
const etiquetaEstado = (r: Remito) => (r.borrador ? 'Borrador' : ETIQUETAS_ESTADO[r.estado] ?? r.estado);
/** Stock del remito: un borrador todavía no movió nada (entra al confirmarlo). */
const estadoStock = (r: Remito) => (r.borrador && !r.wmsStatus ? 'Al confirmar' : ESTADO_STOCK[r.wmsStatus ?? ''] ?? r.wmsStatus ?? '—');
/** Motivo del error de ingreso en una línea: la primera línea del texto y, de ella, la primera oración (corta trazas técnicas). */
const motivoError = (texto: string) => {
  const linea = texto.trim().split('\n')[0].trim();
  return linea.match(/^.+?[.!?](?=\s|$)/)?.[0] ?? linea;
};
const fechaHora = (v?: string | null) => (v ? new Date(v).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const nombreRemito = (r: Remito) => r.numeroRemito || `#${r.id}`;
/** Resumen para confirmar: "2 ítems (5 unidades): Tornillo × 3, Tuerca × 2". */
function resumenItems(r: Remito): string {
  const items = r.items ?? [];
  const unidades = items.reduce((t, i) => t + (parseFloat(i.cantidad) || 0), 0);
  const detalle = items.map(i => `${i.descripcion} × ${parseFloat(i.cantidad) || 0}`).join(', ');
  return `${items.length} ${items.length === 1 ? 'ítem' : 'ítems'} (${unidades} ${unidades === 1 ? 'unidad' : 'unidades'}): ${detalle}`;
}
/** Resultado de POST remitos/:id/eliminar-forzado (sólo admin). */
interface BajaForzada { ok: boolean; faltantes?: { sku: string; cantidad: number }[]; negativos?: string[] }
/** El 400 del borrado porque lo que ingresó ya no está: un admin lo puede borrar igual. */
const esErrorDeStock = (mensaje: string) => /No se puede eliminar el remito /.test(mensaje);
/** Qué pasa con el stock al eliminar el remito (lo decide el backend: ver eliminarRemito). */
function avisoEliminar(r: Remito): string {
  if (r.ordenCompraId) {
    // Lo decide el backend según la recepción del depósito, no el estado del remito (puede haber quedado a medias).
    const ids = idsOC(r);
    return `Los ítems vuelven a quedar pendientes en ${ids.length > 1 ? 'las OC' : 'la OC'} ${listaOC(ids)}. `
      + 'Si la mercadería de este remito ya ingresó al depósito, se descuenta del Stock físico. '
      + 'Si ya no está (se vendió o se movió), no se puede borrar.';
  }
  if (r.wmsStatus === 'ingresado') {
    return 'Se descuenta del Stock físico lo que ingresó con este remito. Si ya no está (se vendió o se movió), no se puede borrar.';
  }
  return 'Este remito no movió stock: se borra sin tocar el stock.';
}
interface LineaOC { id:number; descripcion:string; sku?:string; codigoProveedor?:string; ean?:string; cantidad:string; unidadMedida?:string; cantidadPendiente?:number; cantidadRecibida?:number; }
interface OC { id: number; proveedorId: number; estado: string; fecha: string; numeroOrden?:string; condicionPago?:string; moneda?:string; proveedorRazonSocial?:string; items?: LineaOC[]; }
/** SKU del catálogo desde remitos/skus: pide remitos:ver (no catalogo:ver) y no trae costos ni precios. */
const URL_SKUS = '/api/v3/remitos/skus';

const normClave = (v?: string) => (v || '').trim().toLowerCase();

/**
 * OC del selector a partir de remitos/pendientes-de-recibir: pide remitos:ver (no compras:ver) y no trae importes,
 * así el operador de remitos puede cargar un remito contra una OC sin acceso a Compras.
 */
const aOC = (o: OrdenPendiente): OC => ({
  id: o.id, proveedorId: o.proveedor.id, estado: o.estado, fecha: o.fecha, numeroOrden: o.numeroOrden ?? undefined,
  proveedorRazonSocial: o.proveedor.nombre,
  items: o.lineas.map(l => ({
    id: l.id, descripcion: l.descripcion, sku: l.sku ?? undefined, codigoProveedor: l.codigoProveedor ?? undefined,
    // Lo pendiente sin descontar los borradores (el que se sigue cargando es uno de ellos), como antes de mostrarlos aparte.
    cantidad: String(l.pedido), unidadMedida: l.unidadMedida, cantidadPendiente: Math.max(0, Math.round((l.pedido - l.recibido) * 1e4) / 1e4), cantidadRecibida: l.recibido,
  })),
});

/** Ítem del remito con lo pendiente de una línea de la OC (la cantidad pendiente como recibida). */
const lineaComoItem = (l: LineaOC) => ({
  descripcion: l.descripcion, sku: l.sku || '', codigoProveedor: l.codigoProveedor || '', ean: l.ean || '',
  cantidad: String(l.cantidadPendiente ?? l.cantidad), unidadMedida: l.unidadMedida || 'UN',
  cantidadOrdenada: l.cantidad, cantidadPendiente: l.cantidadPendiente, ordenCompraItemId: l.id,
} as any);

/** Le pone a un ítem del remito la línea de OC que recibe (una OC puede repetir el SKU en varias líneas). */
const aplicarLinea = (item: any, linea: LineaOC) => ({
  ...conSku(item, linea.sku || item.sku || ''), ordenCompraItemId: linea.id, descripcion: item.descripcion || linea.descripcion,
  codigoProveedor: item.codigoProveedor || linea.codigoProveedor || '', unidadMedida: item.unidadMedida || linea.unidadMedida || 'UN',
  cantidadOrdenada: linea.cantidad, cantidadPendiente: linea.cantidadPendiente,
});

/**
 * Asigna a cada ítem sin línea (importado del PDF o agregado a mano) la primera línea de la OC con el
 * mismo SKU o código de proveedor que todavía tenga pendiente; si ninguna tiene, la primera que coincida.
 * El usuario la puede cambiar en la columna "Línea OC".
 */
const asignarLineasOC = (items: any[], lineas: LineaOC[]) => {
  const restante = new Map(lineas.map(l => [l.id, Number(l.cantidadPendiente ?? l.cantidad)]));
  const consumir = (id: number, item: any) => restante.set(id, (restante.get(id) ?? 0) - (parseFloat(item.cantidad || '0') || 0));
  return items.map(item => {
    const actual = item.ordenCompraItemId ? lineas.find(l => l.id === item.ordenCompraItemId) : undefined;
    if (actual) { consumir(actual.id, item); return item; }
    const claves = [normClave(item.sku), normClave(item.codigoProveedor)].filter(Boolean);
    const candidatas = lineas.filter(l => [normClave(l.sku), normClave(l.codigoProveedor)].some(k => k && claves.includes(k)));
    const linea = candidatas.find(l => (restante.get(l.id) ?? 0) > 0) ?? candidatas[0];
    if (!linea) { const { ordenCompraItemId: _sinLinea, ...resto } = item; return resto; }
    consumir(linea.id, item);
    return aplicarLinea(item, linea);
  });
};

type FormRemito = ReturnType<typeof remitoVacio>;
// Fecha de hoy en Argentina: toISOString() es UTC y después de las 21 daría la de mañana.
// Motivo: el de un remito sin OC (con OC es siempre compra). clienteId: sólo en una devolución de cliente.
/** Notas del remito: las que se escribieron y, aparte, los productos que llegaron sin estar en la OC. */
const notasDelRemito = (notas: string, noPedidos: string): string | undefined =>
  [notas.trim(), noPedidos.trim() && `Productos no pedidos (no entran al stock): ${noPedidos.trim()}`].filter(Boolean).join(' · ') || undefined;

/** Cant.Rec mínima: 0 sólo si la línea trae rotas (llegó toda rota); si no, 1. */
const minimoRecibido = (item: any) => (parseFloat(item.cantidadRotura || '0') > 0 ? 0 : 1);

/** Faltante o sobrante del ítem contra lo pendiente de su línea de OC, y las rotas (no entran al stock). */
function DiferenciaItem({ item }: { item: any }) {
  const rotas = parseFloat(item.cantidadRotura || '0') || 0;
  const recibido = parseFloat(item.cantidad || '0') || 0;
  const { tipo, cantidad } = diferenciaRemito(item.cantidadPendiente, recibido, rotas);
  if (!tipo && rotas <= 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1 md:justify-end text-xs">
      {tipo === 'faltante' && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Faltante: {cantidad}</span>}
      {tipo === 'sobrante' && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Sobrante: {cantidad}</span>}
      {rotas > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800">Rotura: {rotas} (no entra al stock)</span>}
      {rotas > 0 && recibido === 0 && <span className="px-2 py-0.5 text-red-800">Llegó todo roto: no entra nada al stock</span>}
    </div>
  );
}

// ordenCompraId: la primera OC elegida; otrasOcs: las que se sumaron después (siempre del mismo proveedor).
const remitoVacio = () => ({ proveedorId:'',clienteId:'',motivo:'compra_sin_oc',ordenCompraId:'',otrasOcs:[] as number[],fecha:hoyAR(),numeroRemito:'',notas:'',noPedidos:'',fechaEntregaAcordada:'',items:[{descripcion:'',cantidad:'1',sku:''} as any] });

/** Formulario de un borrador guardado, para seguir cargándolo. */
const formDeBorrador = (r: Remito): FormRemito => ({
  ...remitoVacio(),
  proveedorId: r.proveedorId != null ? String(r.proveedorId) : '',
  clienteId: r.clienteId != null ? String(r.clienteId) : '',
  motivo: r.ordenCompraId ? 'compra_sin_oc' : (r.motivo ?? 'compra_sin_oc'),
  ordenCompraId: r.ordenCompraId ? String(r.ordenCompraId) : '',
  otrasOcs: idsOC(r).filter(id => id !== r.ordenCompraId),
  fecha: r.fecha.slice(0, 10), numeroRemito: r.numeroRemito ?? '', notas: r.notas ?? '',
  fechaEntregaAcordada: r.fechaEntregaAcordada?.slice(0, 10) ?? '',
  items: agruparItemsConReparto((r.items ?? []).map(i => ({
    descripcion: i.descripcion, sku: i.sku ?? '', varianteId: i.varianteId ?? undefined, codigoProveedor: i.codigoProveedor ?? '', ean: i.ean ?? '', unidadMedida: 'UN',
    cantidad: String(parseFloat(i.cantidad) || 0),
    ...(parseFloat(i.cantidadRotura ?? '0') > 0 && { cantidadRotura: String(parseFloat(i.cantidadRotura!)) }),
    ...(i.ordenCompraItemId != null && { ordenCompraItemId: i.ordenCompraItemId, cantidadOrdenada: i.cantidadOrdenada ?? undefined }),
  } as any))),
});

const etiquetaLinea = (l: LineaOC, idx: number) => `L${idx + 1} · ${l.sku || l.descripcion} · pend. ${l.cantidadPendiente ?? l.cantidad}`;

const estadoColors: Record<string, string> = { pendiente: 'bg-amber-100 text-amber-700', conciliado: 'bg-green-100 text-green-700', rechazado: 'bg-red-100 text-red-700', cancelada: 'bg-slate-100 text-slate-600' };

export function RemitosPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('fecha');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const hasFilters = Object.values(filters).some(v => v);
  const pageSize = 20;
  const { data, loading, total, refetch } = useServerPage<Remito>('/api/v3/remitos', page, pageSize, search, 'q', hasFilters ? filters : undefined);
  const { data: proveedores } = useProveedores();
  const [detailOpen, setDetailOpen] = useState(false); const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Borrador que se sigue cargando en la ventana del remito (null: remito nuevo).
  const [editando, setEditando] = useState<Remito | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [borrandoBorrador, setBorrandoBorrador] = useState(false);
  // El borrado de este remito falló porque su stock ya no está: un admin lo puede eliminar igual.
  const [forzable, setForzable] = useState<{ id: number; mensaje: string } | null>(null);
  const [confirmForzado, setConfirmForzado] = useState(false);
  const [pendientesOpen, setPendientesOpen] = useState(false);
  // "Recibir" en curso (id de la OC): cerrar la lista o desmontar la página invalida el pedido pendiente.
  const [recibiendo, setRecibiendo] = useState<number | null>(null);
  const pedidoRecibir = useRef(0);
  useEffect(() => () => { pedidoRecibir.current++; }, []);
  const cerrarPendientes = () => { pedidoRecibir.current++; setRecibiendo(null); setPendientesOpen(false); };
  const [selected, setSelected] = useState<Remito | null>(null);
  const [ocsRecibibles, setOcsRecibibles] = useState<OC[]>([]);
  const [provSearch, setProvSearch] = useState(''); const [showProv, setShowProv] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(remitoVacio);
  // SKU search (remitos/skus: no prices), limited to the remito supplier unless «otros proveedores»; a client return has
  // no supplier, so it searches everything and offers no checkbox.
  const [incluirOtros, setIncluirOtros] = useState(false);
  useEffect(() => { setIncluirOtros(false); }, [createOpen]);
  const { buscar: buscarSkus, recordar, proveedorDe } = useBuscadorCompra(URL_SKUS, form.proveedorId, incluirOtros && !!form.proveedorId);
  // The footer search: what is typed there (the picked SKU becomes a new item).
  const [skuAbajo, setSkuAbajo] = useState('');
  // Variantes de cada SKU de las líneas (vacío: el SKU no tiene); se piden al fijar el SKU, esperando a que se deje de escribir.
  const [variantesPorSku, setVariantesPorSku] = useState<Record<string, VarianteDeSku[]>>({});
  const skusPedidos = useRef(new Set<string>());
  const skusDeLineas = [...new Set(form.items.map((i: any) => String(i.sku ?? '').trim()).filter(Boolean))].join('|');
  useEffect(() => {
    const espera = setTimeout(() => {
      for (const sku of skusDeLineas.split('|').filter(s => s && !skusPedidos.current.has(s))) {
        skusPedidos.current.add(sku);
        api.get<VariantesDeSku>(`/api/v3/catalogo/${encodeURIComponent(sku)}/variantes`)
          .then(v => setVariantesPorSku(c => ({ ...c, [sku]: (v?.variantes ?? []).filter(x => x.activo) })))
          .catch(() => skusPedidos.current.delete(sku)); // no se guarda el fallo: se reintenta al volver a fijar el SKU
      }
    }, 400);
    return () => clearTimeout(espera);
  }, [skusDeLineas]);
  // Devolución de cliente: el cliente se busca en remitos/clientes a medida que se escribe (como los SKU).
  const [cliSearch, setCliSearch] = useState(''); const [showCli, setShowCli] = useState(false);
  const [clientesEncontrados, setClientesEncontrados] = useState<ClienteRemito[]>([]);
  useEffect(() => {
    const texto = cliSearch.trim();
    if (!showCli || texto.length < 2) { setClientesEncontrados([]); return; }
    let vigente = true;
    const espera = setTimeout(async () => {
      try { const r = await api.get<ClienteRemito[]>(`/api/v3/remitos/clientes?q=${encodeURIComponent(texto)}`); if (vigente) setClientesEncontrados(Array.isArray(r) ? r : []); }
      catch { if (vigente) setClientesEncontrados([]); }
    }, 250);
    return () => { vigente = false; clearTimeout(espera); };
  }, [cliSearch, showCli]);
  // Motivos de un remito sin OC: los del backend (con consignación y terceros si están habilitados); mientras tanto, o si
  // falla, los cuatro de siempre.
  const [motivosSinOC, setMotivosSinOC] = useState<MotivoRemito[]>(MOTIVOS_BASE);
  const motivosPedidos = useRef(false);
  useEffect(() => {
    if (!createOpen || motivosPedidos.current) return;
    motivosPedidos.current = true;
    (async () => {
      try {
        const r = await api.get<MotivoRemito[]>('/api/v3/remitos/motivos');
        const validos = (Array.isArray(r) ? r : []).filter(m => typeof m?.valor === 'string' && typeof m?.etiqueta === 'string');
        if (validos.length > 0) setMotivosSinOC(validos);
      } catch { motivosPedidos.current = false; }
    })();
  }, [createOpen]);
  const esMotivoDeCliente = (valor: string) => (motivosSinOC.find(m => m.valor === valor)?.contraparte ?? 'proveedor') === 'cliente';
  // Remito precargado con "Recibir" (Pendientes de recibir): lo precargado no cuenta como cambio.
  const [precargado, setPrecargado] = useState<{ form: FormRemito; provSearch: string; cliSearch: string } | null>(null);
  // Cerrar el remito nuevo lo descarta. Se compara contra el remito vacío: lo importado de un PDF cuenta como cambio.
  // Si se abrió con "Recibir", se compara contra lo precargado.
  const cerrarNuevo = () => { setCreateOpen(false); setForm(remitoVacio()); setProvSearch(''); setCliSearch(''); setOcsRecibibles([]); setPrecargado(null); setEditando(null); };
  const cierreNuevo = useCierreConfirmado(useHayCambios({ form, provSearch, cliSearch }, createOpen, precargado ?? { form: remitoVacio(), provSearch: '', cliSearch: '' }), cerrarNuevo);
  const obligatorios = useCamposObligatorios(createOpen);
  const sorted = [...data].sort((a: any, b: any) => { const va = a[sortKey] ?? ''; const vb = b[sortKey] ?? ''; const cmp = typeof va === 'string' ? va.localeCompare(vb) : Number(va) - Number(vb); return sortDir === 'asc' ? cmp : -cmp; });
  const filtered = sorted.filter(row => Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())));
  const displayTotal = hasFilters ? filtered.length : total;
  const paged = hasFilters ? filtered.slice((page-1)*pageSize, page*pageSize) : filtered;
  const toast = useToast();
  const avisarImpactoMl = useAvisoImpactoMl();
  const { can, user } = useAuth();
  const esAdmin = user?.role === 'admin';
  // Confirmar un remito: admin con remitos:editar (como el backend, POST remitos/:id/confirmar).
  const puedeConfirmar = esAdmin && can('remitos', 'editar');
  // "Remito #N" en Movimientos de stock: abre ese remito y saca ?remito= para que recargar no lo reabra.
  const [searchParams, setSearchParams] = useSearchParams();
  const remitoEnUrl = Number(searchParams.get('remito'));
  useEffect(() => {
    if (!Number.isInteger(remitoEnUrl) || remitoEnUrl <= 0) return;
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('remito'); return n; }, { replace: true });
    api.get<Remito>(`/api/v3/remitos/${remitoEnUrl}`).then(
      (r) => { setSelected(r); setDetailOpen(true); },
      (e) => toast.addToast(`No se pudo abrir el remito #${remitoEnUrl}: ${extractErrorMessage(e)}`, 'error'),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remitoEnUrl]);
  // «Recibir mercadería» en Hoy: abre Pendientes de recibir y saca ?pendientes= para que recargar o Atrás no la reabra.
  const pendientesEnUrl = searchParams.get('pendientes') === '1';
  useEffect(() => {
    if (!pendientesEnUrl) return;
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('pendientes'); return n; }, { replace: true });
    if (can('remitos', 'ver')) setPendientesOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendientesEnUrl]);
  const { uploading: pdfUploading, importPdf } = usePdfImport('/api/v3/remitos/importar-pdf');
  const [importItemsDraft, setImportItemsDraft] = useState<any[] | null>(null);
  // While the PDF codes are looked up the dialog stays open (and disabled); the answer is applied only if that same
  // draft is still pending (not cancelled), with the OC lines of that moment.
  const [verificandoPdf, setVerificandoPdf] = useState(false);
  const borradorPdf = useRef<any[] | null>(null);
  borradorPdf.current = importItemsDraft;
  const verificandoPdfRef = useRef(false);
  const ocsAlImportar = useRef<{ ocActual?: OC; lineas: LineaOC[] }>({ lineas: [] });

  const filteredProvs = (proveedores??[]).filter(p=>p.nombre.toLowerCase().includes(provSearch.toLowerCase())||String(p.id).includes(provSearch));

  // A picked SKU fills the row (conSku clears the variant split of another SKU); the supplier code is kept if typed.
  const selectSKUForRow = (idx: number, sku: ArticuloCompra, codigoProveedor?: string) => {
    recordar(sku);
    const it = [...form.items];
    it[idx] = {...conSku(it[idx], sku.skuInterno), descripcion: sku.descripcion||it[idx].descripcion, unidadMedida: sku.unidadMedidaBase||it[idx].unidadMedida,
      codigoProveedor: codigoProveedor ?? (it[idx].codigoProveedor || sku.codigoProveedor || '')};
    setForm({...form, items: it});
  };
  const agregarDesdeBuscador = (s: ArticuloCompra) => {
    recordar(s);
    const nuevo={sku:s.skuInterno,descripcion:s.descripcion||'',cantidad:'1',unidadMedida:s.unidadMedidaBase||'UN',codigoProveedor:''};
    setForm({...form,items:[...form.items,ocActual?asignarLineasOC([nuevo],lineasDelRemito)[0]:nuevo]});
    setSkuAbajo('');
  };

  /** OC recibibles del proveedor (null si falló la consulta). */
  const fetchOCs = async (proveedorId: number): Promise<OC[] | null> => {
    try { const r = await api.get<OrdenPendiente[]>(`/api/v3/remitos/pendientes-de-recibir?proveedor_id=${proveedorId}`); return (r??[]).map(aOC); }
    catch { return null; }
  };
  const loadOCs = async (proveedorId: number) => setOcsRecibibles((await fetchOCs(proveedorId)) ?? []);

  const ocActual = ocsRecibibles.find(o=>o.id===parseInt(form.ordenCompraId));
  // OC del remito (la primera y las sumadas), sus líneas y las OC del proveedor que todavía se pueden sumar
  const ocsDelRemito = ocActual ? [ocActual, ...form.otrasOcs.map(id => ocsRecibibles.find(o => o.id === id)).filter((o): o is OC => !!o)] : [];
  const lineasDelRemito = ocsDelRemito.flatMap(o => o.items ?? []);
  ocsAlImportar.current = { ocActual, lineas: lineasDelRemito };
  const ocsParaSumar = ocActual ? ocsRecibibles.filter(o => !ocsDelRemito.some(x => x.id === o.id)) : [];
  const ocDeItem = (item: any) => ocsDelRemito.find(o => (o.items ?? []).some(l => l.id === item.ordenCompraItemId));

  const avisarSinLinea = (items: any[]) => {
    const sinLinea = items.filter(i => i.descripcion && !i.ordenCompraItemId).length;
    if (sinLinea > 0) toast.addToast(`${sinLinea} ítem(s) sin línea de OC: elegila en la columna "Línea OC"`, 'info');
  };

  /**
   * Elige la OC del remito y carga sus líneas pendientes como ítems. Devuelve el formulario resultante.
   * `ocs` y `base` sirven para "Recibir", que elige la OC recién cargada sobre un remito nuevo en el mismo paso.
   */
  const selectOC = (ocId: number, ocs: OC[] = ocsRecibibles, base: FormRemito = form): FormRemito => {
    let nuevo: FormRemito;
    if (!ocId) {
      // Manual (sin OC): los ítems no reciben líneas
      nuevo = {...base,ordenCompraId:'',otrasOcs:[],items:base.items.map(({ordenCompraItemId:_l,cantidadPendiente:_p,...i}:any)=>i)};
      setForm(nuevo);
      return nuevo;
    }
    const oc = ocs.find(o=>o.id===ocId);
    const importados = base.items.filter((i:any)=>i.importado && (i.descripcion || i.sku));
    if (oc?.items && importados.length > 0) {
      // Ítems importados del PDF: se asignan a las líneas de la OC en vez de reemplazarlos
      const items = asignarLineasOC(importados, oc.items);
      nuevo = {...base,ordenCompraId:String(ocId),otrasOcs:[],items};
      avisarSinLinea(items);
    }
    else if (oc?.items) nuevo = {...base,ordenCompraId:String(ocId),otrasOcs:[],items:oc.items.map(lineaComoItem)};
    else nuevo = {...base,ordenCompraId:String(ocId),otrasOcs:[]};
    setForm(nuevo);
    return nuevo;
  };

  /**
   * Suma otra OC del mismo proveedor al remito (decisión del dueño 2026-10-09). Si hay ítems importados del PDF sin
   * línea, se buscan en la OC sumada; si no, sus líneas pendientes se agregan como ítems.
   */
  const sumarOC = (ocId: number) => {
    const oc = ocsRecibibles.find(o => o.id === ocId);
    if (!oc) return;
    const lineas = oc.items ?? [];
    const sinLinea = form.items.filter((i: any) => i.importado && !i.ordenCompraItemId && (i.descripcion || i.sku));
    let items: any[];
    if (sinLinea.length > 0) {
      const asignados = asignarLineasOC(sinLinea, lineas);
      let k = 0;
      items = form.items.map((i: any) => (sinLinea.includes(i) ? asignados[k++] : i));
      avisarSinLinea(items);
    } else {
      items = [...form.items.filter((i: any) => i.descripcion || i.sku), ...lineas.map(lineaComoItem)];
    }
    setForm({ ...form, otrasOcs: [...form.otrasOcs, ocId], items });
  };

  /** Saca del remito una OC sumada y los ítems que recibían sus líneas. */
  const quitarOC = (ocId: number) => {
    const ids = new Set((ocsRecibibles.find(o => o.id === ocId)?.items ?? []).map(l => l.id));
    const items = form.items.filter((i: any) => !ids.has(i.ordenCompraItemId));
    setForm({ ...form, otrasOcs: form.otrasOcs.filter(id => id !== ocId), items: items.length > 0 ? items : remitoVacio().items });
  };

  /**
   * "Recibir" en Pendientes de recibir: cierra esa ventana y abre el remito nuevo con el proveedor y la OC
   * elegidos por el mismo camino que a mano (fetchOCs + selectOC), con la fecha de hoy en Argentina (la del remito vacío) y la
   * entrega acordada de la OC. La lista queda abierta hasta tener las OC, así las dos ventanas no se apilan; la respuesta
   * se descarta si mientras tanto se cerró la lista o empezó otro "Recibir".
   */
  const recibirOC = async (orden: OrdenPendiente) => {
    const pedido = ++pedidoRecibir.current;
    setRecibiendo(orden.id);
    const ocs = await fetchOCs(orden.proveedor.id);
    if (pedido !== pedidoRecibir.current) return;
    setRecibiendo(null);
    if (!ocs) { toast.addToast('No se pudieron cargar las OC del proveedor', 'error'); return; }
    if (!ocs.some(o => o.id === orden.id)) { toast.addToast(`La OC #${orden.id} ya no tiene mercadería pendiente de recibir`, 'info'); return; }
    setOcsRecibibles(ocs);
    const base: FormRemito = { ...remitoVacio(), proveedorId: String(orden.proveedor.id), fechaEntregaAcordada: orden.fechaEntrega ?? '' };
    const conOC = selectOC(orden.id, ocs, base);
    setProvSearch(orden.proveedor.nombre);
    setCliSearch('');
    setPrecargado({ form: conOC, provSearch: orden.proveedor.nombre, cliSearch: '' });
    setPendientesOpen(false);
    setCreateOpen(true);
  };

  /**
   * "Seguir cargando" un borrador: lo abre en la misma ventana que el alta, con sus OC (si tiene proveedor) y lo pendiente
   * de cada línea. Lo cargado no cuenta como cambio: cerrar sin tocar nada no pregunta.
   */
  const seguirCargando = async (r: Remito) => {
    let nuevo = formDeBorrador(r);
    if (r.proveedorId) {
      const ocs = (await fetchOCs(r.proveedorId)) ?? [];
      setOcsRecibibles(ocs);
      const lineas = ocs.flatMap(o => o.items ?? []);
      nuevo = { ...nuevo, items: nuevo.items.map((i: any) => {
        const linea = lineas.find(l => l.id === i.ordenCompraItemId);
        return linea ? { ...i, cantidadPendiente: linea.cantidadPendiente, unidadMedida: linea.unidadMedida || 'UN' } : i;
      }) };
    } else setOcsRecibibles([]);
    const prov = r.proveedor?.nombre ?? ''; const cli = r.cliente?.nombre ?? '';
    setForm(nuevo); setProvSearch(prov); setCliSearch(cli);
    setPrecargado({ form: nuevo, provSearch: prov, cliSearch: cli });
    setEditando(r); setDetailOpen(false); setCreateOpen(true);
  };

  const elegirLinea = (idx: number, lineaId: string) => {
    const it = [...form.items];
    const linea = lineasDelRemito.find(l => l.id === parseInt(lineaId));
    if (linea) it[idx] = aplicarLinea(it[idx], linea);
    else { const { ordenCompraItemId: _l, cantidadPendiente: _p, ...resto } = it[idx] as any; it[idx] = resto; }
    setForm({...form, items: it});
  };

  /**
   * Cambia el motivo de un remito sin OC. Pasar a una devolución de cliente (o salir de ella) cambia la contraparte:
   * se limpian el proveedor y sus OC, o el cliente.
   */
  const elegirMotivo = (valor: string) => {
    const aCliente = esMotivoDeCliente(valor);
    if (aCliente === esMotivoDeCliente(form.motivo)) { setForm({ ...form, motivo: valor }); return; }
    if (aCliente) {
      setForm({ ...form, motivo: valor, proveedorId: '', ordenCompraId: '' });
      setProvSearch(''); setOcsRecibibles([]);
    } else {
      setForm({ ...form, motivo: valor, clienteId: '' });
      setCliSearch('');
    }
  };

  // Obligatorios del remito nuevo (backend: CreateRemitoDto, crearRemito y validarMotivoRemito). Solo se mandan los
  // ítems con descripción y el remito necesita al menos uno; con OC cada uno lleva su línea; sin OC, si la mercadería
  // suma al stock propio, su SKU. La contraparte es el proveedor, o el cliente en una devolución.
  const remitoConOC = !!form.ordenCompraId;
  const remitoSinItems = !form.items.some((i: any) => i.descripcion);
  const pideSku = !remitoConOC && !MOTIVOS_SIN_STOCK_PROPIO.includes(form.motivo);
  // Ayuda interactiva del remito nuevo (ayuda/guias/remito.ts): lo que sus pasos necesitan saber del formulario.
  const contextoAyuda: ContextoRemito = {
    conOC: remitoConOC, deCliente: !remitoConOC && esMotivoDeCliente(form.motivo), pideSku, hayItems: !remitoSinItems, motivos: motivosSinOC,
  };

  /** Guarda el remito en borrador (nuevo o el que se sigue cargando); con `confirmar` (admin), además lo confirma. */
  const guardar = async (confirmar: boolean) => {
    if (!obligatorios.validar()) return;
    const conOC = !!form.ordenCompraId;
    // Con OC el motivo es siempre compra; sin OC, el elegido.
    const motivo = conOC ? 'compra' : form.motivo;
    // La contraparte, la línea de la OC de cada ítem y el SKU sin OC ya los pidió validar() (el aviso nombra el campo).
    const deCliente = !conOC && esMotivoDeCliente(motivo);
    // OC principal: la primera del remito que recibe algún ítem (si se borraron los de la primera, la siguiente)
    const lineas = form.items.filter(i => i.descripcion);
    for (const l of lineas) {
      const recibido = parseFloat(l.cantidad) || 0;
      const { excede, repartidas } = resumenReparto(recibido, l.reparto ?? {});
      if (excede) { toast.addToast(`El reparto (${repartidas}) supera lo recibido (${recibido}).`, 'error'); return; }
    }
    const aEnviar = lineas.flatMap(expandirLinea);
    const principal = ocsDelRemito.find(o => aEnviar.some(i => ocDeItem(i)?.id === o.id))?.id ?? (form.ordenCompraId ? parseInt(form.ordenCompraId) : undefined);
    const body = {
        ...(deCliente ? { clienteId: parseInt(form.clienteId) } : { proveedorId: parseInt(form.proveedorId) }),
        motivo, ordenCompraId:conOC?principal:undefined,
        // Productos que llegaron y no están en la OC: quedan anotados en las notas y no entran al stock (DECISIONES 2026-10-09).
        fecha:form.fecha, numeroRemito:form.numeroRemito||undefined, notas:notasDelRemito(form.notas, conOC ? form.noPedidos : ''),
        fechaEntregaAcordada:form.fechaEntregaAcordada||undefined,
        items:aEnviar.map(i=>({descripcion:i.descripcion,cantidad:parseFloat(i.cantidad),sku:i.sku||undefined,cantidadOrdenada:(i as any).cantidadOrdenada?parseFloat((i as any).cantidadOrdenada):undefined,cantidadRotura:parseFloat((i as any).cantidadRotura)>0?parseFloat((i as any).cantidadRotura):undefined,ordenCompraItemId:conOC?(i as any).ordenCompraItemId:undefined,varianteId:(i as any).varianteId??undefined}))
    };
    setSubmitting(true);
    try {
      const guardado = editando
        ? await api.put<{ id: number }>(`/api/v3/remitos/${editando.id}/borrador`, body)
        : await api.post<{ id: number }>('/api/v3/remitos', body);
      refetch();
      // Cierra sin preguntar y limpia también las OC recibibles en memoria: si quedan cargadas, el <select>
      // de "OC Recibible" las sigue ofreciendo con las cantidades pendientes viejas (de antes de este remito),
      // aunque el proveedor ya se haya reseteado — forzamos a reelegir proveedor para traerlas de nuevo.
      const id = guardado?.id ?? editando?.id;
      cerrarNuevo();
      if (!confirmar) { toast.addToast('Borrador guardado', 'success'); return; }
      try {
        const confirmado = await api.post<Remito>(`/api/v3/remitos/${id}/confirmar`, {});
        avisarConfirmado(confirmado, aEnviar.map(i => i.sku));
        refetch();
      } catch (e) {
        // La confirmación pudo guardarse y fallar después (p. ej. antes de la recepción del depósito): se mira el remito.
        const borrador = await api.get<Remito>(`/api/v3/remitos/${id}`).then(r => r?.borrador, () => undefined);
        toast.addToast(
          borrador === true ? `El remito quedó guardado como borrador, pero no se pudo confirmar: ${extractErrorMessage(e)}`
            : borrador === false ? 'Se confirmó pero falló el ingreso al stock: reintentalo desde Configuración → Mantenimiento'
              : `No se pudo confirmar: ${extractErrorMessage(e)}. Revisá en la lista si quedó confirmado antes de reintentar`,
          'error',
        );
        refetch();
      }
    } catch (e) { toast.addToast(extractErrorMessage(e),'error'); }
    finally { setSubmitting(false); }
  };

  /**
   * Con OC el stock entra por la recepción del depósito: si falló, se avisa dónde reintentarlo. Si entró, además se avisa
   * (sin bloquear) si no se va a reflejar en alguna publicación de Mercado Libre (`skus`: los del remito).
   */
  const avisarConfirmado = (r: Remito | null | undefined, skus: Array<string | null | undefined>) => {
    if (r?.wmsStatus === 'error') { toast.addToast('Remito confirmado, pero su mercadería no entró al depósito: reintentalo desde Configuración → Mantenimiento', 'info'); return; }
    toast.addToast('Remito confirmado: suma al stock y pasa a Facturas', 'success');
    avisarImpactoMl(skus);
  };

  const handleConfirmar = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      avisarConfirmado(await api.post<Remito>(`/api/v3/remitos/${selected.id}/confirmar`, {}), (selected.items ?? []).map(i => i.sku));
      setConfirmando(false); setDetailOpen(false); refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); setConfirmando(false); }
    finally { setSubmitting(false); }
  };

  const handleEliminarBorrador = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.delete(`/api/v3/remitos/${selected.id}/borrador`);
      toast.addToast('Borrador eliminado', 'success');
      setBorrandoBorrador(false); setDetailOpen(false); refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); setBorrandoBorrador(false); }
    finally { setSubmitting(false); }
  };

  const handleEliminarForzado = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const r = await api.post<BajaForzada>(`/api/v3/remitos/${selected.id}/eliminar-forzado`, {});
      const negativos = r?.negativos ?? []; const faltantes = r?.faltantes ?? [];
      toast.addToast('Remito eliminado'
        + (negativos.length ? `. Quedaron con disponible negativo: ${negativos.join(', ')}` : '')
        + (faltantes.length ? `. En el depósito faltaron: ${faltantes.map(f => `${f.sku} × ${f.cantidad}`).join(', ')}` : ''), 'success');
      setConfirmForzado(false); setForzable(null); setDetailOpen(false); refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); setConfirmForzado(false); }
    finally { setSubmitting(false); }
  };

  const handleDeleteRemito = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const r = await api.delete<{ ok: boolean; stockRevertido?: boolean }>(`/api/v3/remitos/${selected.id}`);
      toast.addToast(r?.stockRevertido ? 'Remito eliminado: se descontó del Stock físico lo que había ingresado' : 'Remito eliminado', 'success');
      setDetailOpen(false); setConfirmDelete(false); refetch();
    } catch (e) {
      const mensaje = extractErrorMessage(e);
      toast.addToast(mensaje, 'error');
      setConfirmDelete(false);
      // Su stock ya no está (se vendió, se reservó o se movió): un admin con remitos:eliminar lo puede eliminar igual.
      if (esErrorDeStock(mensaje) && esAdmin && can('remitos', 'eliminar')) setForzable({ id: selected.id, mensaje });
    }
    finally { setSubmitting(false); }
  };

  const addItem = () => setForm({...form,items:[...form.items,{descripcion:'',cantidad:'1',sku:'',codigoProveedor:'',unidadMedida:'UN'}]});

  const handlePdfImport = async (file: File) => {
    try {
      const data = await importPdf(file);
      if (data?.detectado) {
        const d = data.detectado;
        if (d.error) {
          toast.addToast(d.error, 'info');
          return;
        }
        if (d.numeroRemito) setForm(f => ({...f, numeroRemito: d.numeroRemito}));
        if (d.fecha) {
          const parts = d.fecha.split('/');
          if (parts.length === 3) {
            setForm(f => ({...f, fecha: `${parts[2]}-${parts[1]}-${parts[0]}`}));
          }
        }
        if (d.cuit) {
          const prov = (proveedores??[]).find((p:any) => p.cuit === d.cuit);
          if (prov) {
            setForm(f => ({...f, proveedorId: String(prov.id)}));
            setProvSearch(prov.nombre);
            loadOCs(prov.id);
            toast.addToast(`Proveedor encontrado: ${prov.nombre} (CUIT: ${d.cuit})`, 'success');
          } else {
            toast.addToast(`CUIT detectado: ${d.cuit} — no encontrado en proveedores`, 'info');
          }
        }
        if (d.proveedorNombre && !form.proveedorId) setProvSearch(d.proveedorNombre);
        if (d.hasItems && d.items?.length > 0) {
          setImportItemsDraft(d.items);
        }
        toast.addToast(`PDF procesado. Campos detectados: ${Object.keys(d).filter(k => k !== 'hasItems' && k !== 'items').join(', ')}`, 'success');
        setCreateOpen(true);
      }
    } catch { /* toast already shown by hook */ }
  };

  /**
   * The PDF codes as articles (exact codes, with the remito's supplier): one article → its SKU; a code of several
   * suppliers keeps the code as written and is listed in one notice; unknown ones are listed too. Never blocks the load.
   */
  const resolverSkusImportados = async (codigos: string[], draft: any[]): Promise<Map<string, string>> => {
    if (codigos.length === 0) return new Map();
    try {
      const { sku, ambiguos, noEncontrados } = await resolverCodigosDocumento(URL_SKUS, codigos, form.proveedorId, recordar, { incluirPacks: true });
      if (borradorPdf.current !== draft) return sku; // cancelled meanwhile: nothing to warn about
      if (noEncontrados.length > 0) toast.addToast(`SKU no encontrados: ${noEncontrados.join(', ')}`, 'error');
      if (ambiguos.length > 0) toast.addToast(ambiguos.join('\n'), 'error');
      return sku;
    } catch {
      if (borradorPdf.current === draft) toast.addToast('No se pudieron verificar los códigos del PDF', 'error');
      return new Map();
    }
  };

  const confirmImportItems = async () => {
    const draft = importItemsDraft;
    if (!draft || verificandoPdfRef.current) return;
    verificandoPdfRef.current = true;
    setVerificandoPdf(true);
    try {
      const codigos = [...new Set<string>(draft.map((item: any) => item.sku).filter(Boolean))];
      const resueltos = await resolverSkusImportados(codigos, draft);
      if (borradorPdf.current !== draft) return; // cancelled (or replaced) while looking up
      const { ocActual, lineas } = ocsAlImportar.current;
      const mapped = draft.map((item: any) => ({
        descripcion: item.descripcion || '',
        cantidad: item.cantidad || '1',
        sku: resueltos.get(item.sku) ?? (item.sku || ''),
        // El lector de PDF lo devuelve como codProveedor
        codigoProveedor: item.codProveedor || item.codigoProveedor || '',
        unidadMedida: item.unidadMedida || 'UN',
        importado: true,
      }));
      // Con OC elegida, cada ítem importado se asigna a una línea (el usuario la puede cambiar)
      const items = ocActual ? asignarLineasOC(mapped, lineas) : mapped;
      if (ocActual) avisarSinLinea(items);
      setForm(f => ({...f, items}));
      setImportItemsDraft(null);
    } finally {
      verificandoPdfRef.current = false;
      setVerificandoPdf(false);
    }
  };

  // Con varias OC, los ítems se muestran agrupados por OC (y aparte los que todavía no tienen línea).
  const gruposDeItems: { clave: string; titulo: string | null; indices: number[] }[] = ocsDelRemito.length > 1
    ? [
      ...ocsDelRemito.map(oc => ({ clave: `oc-${oc.id}`, titulo: `OC #${oc.id}`, indices: form.items.flatMap((it, i) => (ocDeItem(it)?.id === oc.id ? [i] : [])) })),
      { clave: 'sin-linea', titulo: 'Sin línea de OC', indices: form.items.flatMap((it, i) => (ocDeItem(it) ? [] : [i])) },
    ].filter(g => g.indices.length > 0)
    : [{ clave: 'todos', titulo: null, indices: form.items.map((_, i) => i) }];
  const opcionesLinea = ocsDelRemito.length > 1
    ? ocsDelRemito.map(oc => (<optgroup key={oc.id} label={`OC #${oc.id}`}>{(oc.items ?? []).map((l, idx) => (<option key={l.id} value={l.id}>{etiquetaLinea(l, idx)}</option>))}</optgroup>))
    : lineasDelRemito.map((l, idx) => (<option key={l.id} value={l.id}>{etiquetaLinea(l, idx)}</option>));

  const columns = [
    { key:'numeroRemito',header:'Numero',render:(r:Remito)=>r.numeroRemito??`#${r.id}`,help:'Número de remito' },
    { key:'proveedor',header:'Proveedor / Cliente',render:(r:Remito)=>contraparte(r),help:'Quién entregó la mercadería: el proveedor o, en una devolución, el cliente' },
    { key:'motivo',header:'Motivo',render:(r:Remito)=>etiquetaMotivo(r),filterable:false,help:'Compra (con OC), compra sin OC, bonificación, devolución de cliente u otro ingreso' },
    { key:'ordenCompraId',header:'OC #',render:(r:Remito)=>idsOC(r).join(', ')||'—',help:'Órdenes de compra que trae el remito (todas del mismo proveedor)' },
    { key:'fecha',header:'Fecha',render:(r:Remito)=>r.fecha?new Date(r.fecha).toLocaleDateString('es-AR'):'—',help:'Fecha de recepción' },
    { key:'estado',header:'Estado',render:(r:Remito)=>(<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estadoColors[r.estado]??'bg-slate-100 text-slate-600'}`}>{etiquetaEstado(r)}</span>), filterable: false, help:'Borrador: se sigue cargando y todavía no sumó al stock; un administrador lo confirma. Confirmado: ya suma al stock y pasa a Facturas' },
    { key:'wmsStatus',header:'Stock',render:(r:Remito)=>(<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.wmsStatus==='ok'||r.wmsStatus==='ingresado'?'bg-green-100 text-green-700':r.wmsStatus==='error'?'bg-red-100 text-red-700':'bg-slate-100 text-slate-600'}`}>{estadoStock(r)}</span>), filterable: false, help:'Si la mercadería ya sumó al stock (depósito y stock para vender); un borrador suma al confirmarlo' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3">
        <div><h1 className="text-2xl font-bold text-slate-800 inline">Remitos</h1><HelpTip tips={['Seleccioná un proveedor, después una OC Recibible para cargar items automáticamente','Si el remito trae mercadería de varias OC del mismo proveedor, sumalas con "Sumar otra OC"','Editá las cantidades recibidas si difieren de la OC','Sin OC elegí el motivo: compra sin OC, bonificación o muestra, devolución de cliente u otro ingreso','El remito se guarda en borrador: se puede seguir cargando y suma al stock cuando un administrador lo confirma','Cada ítem sin OC necesita su SKU','La mercadería que devuelve un cliente entra con un remito de Devolución de cliente (la nota de crédito no mueve stock)','La columna Stock muestra si la mercadería ya sumó al stock']} /><VerInstructivo seccion="remitos" /><p className="text-slate-500 text-sm mt-1">Recepción de mercadería: se carga en borrador y la confirma un administrador</p></div>
        <div className="flex flex-wrap items-center gap-3 gap-y-2">
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Buscar..." className="px-3 py-2 border rounded-lg text-sm w-full sm:w-56" />
          <MenuImportar ocupado={pdfUploading} opciones={[
            { etiqueta: 'PDF del remito', descripcion: 'Completa un remito nuevo con los datos del PDF', archivo: { accept: '.pdf', onArchivo: handlePdfImport }, deshabilitado: !can('remitos', 'crear') },
          ]} />
          <Button variant="ghost" onClick={()=>setPendientesOpen(true)} disabled={!can('remitos', 'ver')} title={!can('remitos', 'ver') ? 'No tenés permiso para realizar esta acción' : 'OC con mercadería pendiente de recibir (sin precios)'}>Pendientes de recibir</Button>
          <Button onClick={()=>setCreateOpen(true)} disabled={!can('remitos', 'crear')} title={!can('remitos', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}>+ Nuevo Remito</Button>
        </div>
      </div>
      <DataTable columns={columns} data={paged} loading={loading} onRowClick={r=>{setSelected(r);setForzable(null);setDetailOpen(true)}}
        sortKey={sortKey} sortDir={sortDir} onSort={(k) => { if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSortKey(k); setSortDir('asc'); } }}
        filters={filters} onFilterChange={(k,v)=>setFilters(f=>v?{...f,[k]:v}:Object.fromEntries(Object.entries(f).filter(([key])=>key!==k)))} />
      <Pagination page={page} pageSize={pageSize} total={displayTotal} onChange={setPage} />
      <Modal open={createOpen} cierre={cierreNuevo} title={editando ? `Borrador ${nombreRemito(editando)}` : 'Nuevo Remito'} size="xl">
        <div {...obligatorios.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium mb-1">Motivo</label>
              {/* Con OC el motivo es siempre Compra; sin OC se elige (una devolución de cliente lleva cliente, no proveedor). */}
              <select aria-label="Motivo" value={form.ordenCompraId ? 'compra' : form.motivo} onChange={e=>elegirMotivo(e.target.value)} disabled={!!form.ordenCompraId} className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-50" title={form.ordenCompraId ? 'Con OC el remito es una compra' : 'Qué mercadería entra y quién la entrega'}>
                {form.ordenCompraId ? <option value="compra">Compra</option> : motivosSinOC.map(m=><option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
              </select></div>
            {!form.ordenCompraId && esMotivoDeCliente(form.motivo) ? (
            <div className="relative"><label className="block text-sm font-medium mb-1">Cliente<Obligatorio /></label>
              <input aria-label="Cliente" {...faltaSi(!form.clienteId)} value={cliSearch} onChange={e=>{setCliSearch(e.target.value);setShowCli(true);setForm({...form,clienteId:''})}} onFocus={()=>setShowCli(true)} onBlur={()=>setTimeout(()=>setShowCli(false),200)} placeholder="Buscar cliente por nombre o CUIT..." className="w-full px-3 py-2 border rounded-lg" />
              {showCli && clientesEncontrados.length>0 && (<div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">{clientesEncontrados.map(c=>(<div key={c.id} onClick={()=>{setForm({...form,clienteId:String(c.id)});setCliSearch(c.nombre);setShowCli(false)}} className="px-3 py-1.5 text-sm hover:bg-brand-50 cursor-pointer">{c.nombre}{c.cuit ? ` (${c.cuit})` : ''}</div>))}</div>)}
            </div>
            ) : (<>
            <div className="relative"><label className="block text-sm font-medium mb-1">Proveedor<Obligatorio /></label>
              <input aria-label="Proveedor" {...faltaSi(!form.proveedorId)} value={provSearch} onChange={e=>{setProvSearch(e.target.value);setShowProv(true)}} onFocus={()=>setShowProv(true)} onBlur={()=>setTimeout(()=>setShowProv(false),200)} placeholder="Buscar por nombre o ID..." className="w-full px-3 py-2 border rounded-lg" />
              {showProv && filteredProvs.length>0 && (<div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">{filteredProvs.map(p=>(<div key={p.id} onClick={()=>{setForm({...form,proveedorId:String(p.id),ordenCompraId:''});setProvSearch(p.nombre);setShowProv(false);loadOCs(p.id)}} className="px-3 py-1.5 text-sm hover:bg-brand-50 cursor-pointer">{p.nombre} (ID: {p.id})</div>))}</div>)}
            </div>
            <div><label className="block text-sm font-medium mb-1">OC Recibible</label>
              <select aria-label="OC Recibible" value={form.ordenCompraId} onChange={e=>selectOC(+e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="">— Manual —</option>{ocsRecibibles.map(o=><option key={o.id} value={o.id}>OC #{o.id}</option>)}
              </select></div>
            </>)}
            <div><label className="block text-sm font-medium mb-1">Fecha<Obligatorio /></label><input type="date" aria-label="Fecha" required value={form.fecha} onChange={e=>setForm({...form,fecha:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Nro Remito</label><input aria-label="Nro Remito" value={form.numeroRemito} onChange={e=>setForm({...form,numeroRemito:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Fecha Ent. Acordada</label><input type="date" aria-label="Fecha Ent. Acordada" value={form.fechaEntregaAcordada} onChange={e=>setForm({...form,fechaEntregaAcordada:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Notas</label><input aria-label="Notas" value={form.notas} onChange={e=>setForm({...form,notas:e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
          </div>
          {ocActual && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-2">
              <ul className="space-y-1">
                {ocsDelRemito.map((oc, n) => (
                  <li key={oc.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span><span className="text-slate-500">OC:</span> #{oc.id} — {oc.proveedorRazonSocial||''}</span>
                    <span><span className="text-slate-500">Estado:</span> {oc.estado}</span>
                    {n === 0 && ocsDelRemito.length > 1 && <span className="text-xs text-slate-600">Principal: el remito figura bajo esta OC; también trae renglones de las otras</span>}
                    {n > 0 && <button type="button" onClick={()=>quitarOC(oc.id)} aria-label={`Quitar OC #${oc.id}`} title="Saca esta OC y sus ítems del remito" className="text-xs font-medium text-red-600 hover:text-red-800 underline underline-offset-2">Quitar</button>}
                  </li>
                ))}
              </ul>
              {ocsParaSumar.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-slate-200">
                  <label htmlFor="remito-sumar-oc" className="text-slate-600">¿Llegó mercadería de otra OC de este proveedor en el mismo remito?</label>
                  <select id="remito-sumar-oc" aria-label="Sumar otra OC" value="" onChange={e=>{ if (e.target.value) sumarOC(+e.target.value); }} className="px-3 py-1.5 border rounded-lg bg-white">
                    <option value="">— Elegí una OC —</option>{ocsParaSumar.map(o=><option key={o.id} value={o.id}>OC #{o.id}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
           <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
             <h3 className="text-sm font-semibold text-slate-700">Items</h3>
             {form.proveedorId && <OtrosProveedores checked={incluirOtros} onChange={setIncluirOtros} />}
           </div>
           {ocActual && (<p className="text-xs text-slate-500">Cargados desde {ocsDelRemito.length > 1 ? 'las OC' : 'la OC'} {listaOC(ocsDelRemito.map(o => o.id))} — podés editar cantidades</p>)}
           <div className="overflow-x-auto">
            <div className="hidden md:grid md:grid-cols-12 md:gap-1 text-[10px] text-slate-400 px-1 md:min-w-[700px]">
              <span>SKU{pideSku && <Obligatorio />}</span><span>Cod.Prov</span><span className="col-span-2">Descripción<Obligatorio /></span><span className="text-center">UM</span><span className="col-span-2">Línea OC{remitoConOC && <Obligatorio />}</span><span className="text-right" title="Pendiente de recibir en la línea de la OC">Pend.</span><span className="text-right">Cant.OC</span><span className="text-right">Cant.Rec<Obligatorio /></span><span className="text-right" title="Unidades que llegaron rotas: no entran al stock">Rotas</span><span></span>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto md:min-w-[700px]">
           {gruposDeItems.map(g => (<Fragment key={g.clave}>
            {g.titulo && <h4 className="text-xs font-semibold text-slate-600 pt-2">{g.titulo} <span className="font-normal text-slate-400">· {g.indices.length} {g.indices.length === 1 ? 'ítem' : 'ítems'}</span></h4>}
            {g.indices.map(i => { const item = form.items[i]; return (<div key={i} className="relative rounded-lg border border-slate-200 bg-white p-3 md:border-0 md:bg-transparent md:p-0 md:rounded-none">
             {/* Desktop/tablet row (md and up) */}
             <div className="hidden md:grid md:grid-cols-12 md:gap-1 text-xs">
               <div>
                 <BuscadorSku flotante mostrarCodigo value={item.sku||''} buscar={buscarSkus} onChange={t=>{const it=[...form.items];it[i]=conSku(item,t);setForm({...form,items:it})}} onElegir={s=>selectSKUForRow(i,s)} placeholder="SKU" data-campo="SKU" required={pideSku && !!item.descripcion} className="w-full px-2 py-1.5 border rounded text-xs" title="Escribí 2+ caracteres para buscar en el catálogo" />
                 <ProveedorDelArticulo proveedor={proveedorDe(item.sku)} proveedorDocumentoId={form.proveedorId} />
               </div>
               <div>
                 <BuscadorSku flotante value={item.codigoProveedor||''} buscar={buscarSkus} onChange={t=>{const it=[...form.items];it[i]={...item,codigoProveedor:t};setForm({...form,items:it})}} onElegir={s=>selectSKUForRow(i,s,s.codigoProveedor??item.codigoProveedor??'')} placeholder="Cod.Prov" className="w-full px-2 py-1.5 border rounded text-xs" title="Código SKU del proveedor: escribí 2+ caracteres para buscar" />
               </div>
               <input value={item.descripcion||''} onChange={e=>{const it=[...form.items];it[i]={...item,descripcion:e.target.value};setForm({...form,items:it})}} placeholder="Desc." data-campo="Descripción" required={remitoSinItems} className="w-full px-2 py-1.5 border rounded text-xs col-span-2" />
               <input value={item.unidadMedida||'UN'} onChange={e=>{const it=[...form.items];it[i]={...item,unidadMedida:e.target.value};setForm({...form,items:it})}} className="w-full px-2 py-1.5 border rounded text-xs text-center" />
               <select value={(item as any).ordenCompraItemId??''} onChange={e=>elegirLinea(i,e.target.value)} disabled={!ocActual} required={remitoConOC && !!item.descripcion} data-campo="Línea OC" className="w-full px-1 py-1.5 border rounded text-xs col-span-2" title={ocActual?'Línea de la OC que recibe este ítem':'Elegí una OC para asignar líneas'}>
                 <option value="">{ocActual?'— Elegir línea —':'—'}</option>
                 {opcionesLinea}
               </select>
               <div className="px-2 py-1.5 bg-slate-50 rounded text-right">{(item as any).cantidadPendiente??'—'}</div>
               <div className="px-2 py-1.5 bg-slate-50 rounded text-right">{(item as any).cantidadOrdenada||'—'}</div>
               <input type="number" min={minimoRecibido(item)} step="any" value={item.cantidad||''} onChange={e=>{const it=[...form.items];it[i]={...item,cantidad:e.target.value};setForm({...form,items:it})}} placeholder="Rec." data-campo="Cantidad" required={!!item.descripcion} className="w-full px-2 py-1.5 border rounded text-xs" />
               <input type="number" min="0" step="any" value={(item as any).cantidadRotura||''} onChange={e=>{const it=[...form.items];it[i]={...item,cantidadRotura:e.target.value} as any;setForm({...form,items:it})}} placeholder="Rotas" title="Unidades que llegaron rotas: no entran al stock" className="w-full px-2 py-1.5 border rounded text-xs" />
               <button onClick={()=>{const it=[...form.items];it.splice(i,1);setForm({...form,items:it})}} className="text-red-500 text-xs self-center" title="Eliminar ítem" aria-label="Eliminar ítem">✕</button>
             </div>
             {/* Mobile card (below md) */}
             <div className="md:hidden space-y-2">
               <button onClick={()=>{const it=[...form.items];it.splice(i,1);setForm({...form,items:it})}} className="absolute top-1 right-1 text-red-500 min-w-[2.5rem] min-h-[2.5rem] flex items-center justify-center" title="Eliminar ítem" aria-label="Eliminar ítem">✕</button>
               <div className="pr-12">
                 <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">SKU{pideSku && <Obligatorio />}</label>
                 <BuscadorSku flotante mostrarCodigo value={item.sku||''} buscar={buscarSkus} onChange={t=>{const it=[...form.items];it[i]=conSku(item,t);setForm({...form,items:it})}} onElegir={s=>selectSKUForRow(i,s)} placeholder="SKU" data-campo="SKU" required={pideSku && !!item.descripcion} className="w-full px-2 py-1.5 border rounded text-sm" title="Escribí 2+ caracteres para buscar en el catálogo" />
                 <ProveedorDelArticulo proveedor={proveedorDe(item.sku)} proveedorDocumentoId={form.proveedorId} />
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Cod.Prov</label>
                 <BuscadorSku flotante value={item.codigoProveedor||''} buscar={buscarSkus} onChange={t=>{const it=[...form.items];it[i]={...item,codigoProveedor:t};setForm({...form,items:it})}} onElegir={s=>selectSKUForRow(i,s,s.codigoProveedor??item.codigoProveedor??'')} placeholder="Cod.Prov" className="w-full px-2 py-1.5 border rounded text-sm" title="Código SKU del proveedor: escribí 2+ caracteres para buscar" />
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Descripción<Obligatorio /></label>
                 <input value={item.descripcion||''} onChange={e=>{const it=[...form.items];it[i]={...item,descripcion:e.target.value};setForm({...form,items:it})}} placeholder="Desc." data-campo="Descripción" required={remitoSinItems} className="w-full px-2 py-1.5 border rounded text-sm" />
               </div>
               <div className="grid grid-cols-2 gap-2">
                 <div>
                   <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">UM</label>
                   <input value={item.unidadMedida||'UN'} onChange={e=>{const it=[...form.items];it[i]={...item,unidadMedida:e.target.value};setForm({...form,items:it})}} className="w-full px-2 py-1.5 border rounded text-sm text-center" />
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Cant.OC</label>
                   <div className="px-2 py-1.5 bg-slate-50 rounded text-sm">{(item as any).cantidadOrdenada||'—'}</div>
                 </div>
               </div>
               <div className="grid grid-cols-2 gap-2">
                 <div>
                   <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Cant.Rec<Obligatorio /></label>
                   <input type="number" min={minimoRecibido(item)} step="any" value={item.cantidad||''} onChange={e=>{const it=[...form.items];it[i]={...item,cantidad:e.target.value};setForm({...form,items:it})}} placeholder="Rec." data-campo="Cantidad" required={!!item.descripcion} className="w-full px-2 py-1.5 border rounded text-sm" />
                 </div>
                 <div>
                   <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Pend.</label>
                   <div className="px-2 py-1.5 bg-slate-50 rounded text-sm">{(item as any).cantidadPendiente??'—'}</div>
                 </div>
               </div>
               <div>
                 <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Rotas</label>
                 <input type="number" min="0" step="any" value={(item as any).cantidadRotura||''} onChange={e=>{const it=[...form.items];it[i]={...item,cantidadRotura:e.target.value} as any;setForm({...form,items:it})}} placeholder="Rotas" title="Unidades que llegaron rotas: no entran al stock" className="w-full px-2 py-1.5 border rounded text-sm" />
               </div>
               {ocActual && (
                 <div>
                   <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Línea OC<Obligatorio /></label>
                   <select value={(item as any).ordenCompraItemId??''} onChange={e=>elegirLinea(i,e.target.value)} required={!!item.descripcion} data-campo="Línea OC" className="w-full px-2 py-1.5 border rounded text-sm">
                     <option value="">— Elegir línea —</option>
                     {opcionesLinea}
                   </select>
                 </div>
               )}
             </div>
             {(variantesPorSku[String(item.sku ?? '').trim()]?.length ?? 0) > 0 && (item.reparto
               ? (<>
                   <RepartoVariantes variantes={variantesPorSku[String(item.sku ?? '').trim()]} recibido={parseFloat(item.cantidad) || 0} reparto={item.reparto}
                     onChange={r => { const it=[...form.items]; it[i]={...item,reparto:r} as any; setForm({...form,items:it}); }} />
                   <button type="button" onClick={()=>{const it=[...form.items]; it[i]={...item,reparto:undefined} as any; setForm({...form,items:it});}} className="mt-1 text-xs text-slate-600 underline underline-offset-2">Quitar reparto</button>
                 </>)
               : (<><button type="button" onClick={()=>{const it=[...form.items]; it[i]={...item,reparto:{}} as any; setForm({...form,items:it});}} className="mt-1 text-xs font-medium text-brand-600 hover:underline">Repartir por variante</button><span className="ml-2 text-xs text-slate-500">Opcional: lo que no repartas queda en «Sin asignar».</span></>))}
             <DiferenciaItem item={item} />
            </div>); })}
           </Fragment>))}
            </div>
           </div>
           {remitoConOC && (
             <div>
               <label htmlFor="remito-no-pedidos" className="block text-sm font-medium mb-1">Productos que llegaron y no están en la OC</label>
               <textarea id="remito-no-pedidos" rows={2} value={form.noPedidos} onChange={e=>setForm({...form,noPedidos:e.target.value})} placeholder="Ej.: 6 bowls de cerámica 15 cm" className="w-full px-3 py-2 border rounded-lg text-sm" />
               <p className="text-xs text-slate-500 mt-1">Quedan anotados en las notas del remito. No entran al stock.</p>
             </div>
           )}
           <div className="sticky bottom-0 -mx-6 px-6 bg-white pt-3 pb-1 border-t border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
             <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
               <div className="relative">
                 <BuscadorSku flotante value={skuAbajo} buscar={buscarSkus} onChange={setSkuAbajo} onElegir={agregarDesdeBuscador} placeholder="Buscar SKU del catálogo..." className="px-3 py-2 border rounded-lg text-sm w-full sm:w-56" />
               </div>
               <button onClick={addItem} className="text-brand-600 text-sm font-medium text-left">+ Item manual</button>
               {form.items.some(it=>{const rec=parseFloat(it.cantidad||'0');const ord=parseFloat((it as any).cantidadOrdenada||'0');return ord>0&&rec<ord;})&&(
                 <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                   ⚠ Hay ítems con cantidad menor a la pedida — la OC quedará en estado <strong>parcial</strong>
                 </div>
               )}
             </div>
              <div className="flex flex-wrap gap-3 justify-end"><button onClick={cierreNuevo.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
                <Button data-ayuda="guardar" variant={puedeConfirmar ? 'ghost' : 'primary'} onClick={()=>guardar(false)} loading={submitting} disabled={!can('remitos', 'crear')} title={!can('remitos', 'crear') ? 'No tenés permiso para realizar esta acción' : 'Queda en borrador: se puede seguir cargando y no suma al stock hasta que un administrador lo confirma'}>Guardar borrador</Button>
                {puedeConfirmar && <Button onClick={()=>guardar(true)} loading={submitting} title="Guarda el remito y lo confirma: suma al stock y pasa a Facturas">Guardar y confirmar</Button>}
              </div>
           </div>
         </div>
       </Modal>
       {/* Fuera de la ventana, para quedar arriba de ella en la pila de Escape. Pausada (sin perder el paso) mientras se confirma descartar. */}
       <GuiaInteractiva guia={guiaRemitoNuevo} activa={createOpen && !editando} pausada={cierreNuevo.confirmando} contexto={contextoAyuda}
         acciones={{ asignar: (campo, valor) => { if (campo === 'motivo') elegirMotivo(valor); } }} />
       <Modal open={detailOpen} onClose={()=>setDetailOpen(false)} title={`Remito #${selected?.id||''}`} size="lg">
        {selected&&(<div className="space-y-4"><div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><span className="text-slate-500">Numero:</span><span className="font-medium">{selected.numeroRemito||`#${selected.id}`}</span></div>
          {selected.cliente
            ? <div><span className="text-slate-500">Cliente:</span><span className="font-medium">{selected.cliente.nombre}</span></div>
            : <div><span className="text-slate-500">Proveedor:</span><span className="font-medium">{selected.proveedor?.nombre||'—'}</span></div>}
          <div><span className="text-slate-500">Motivo:</span><span className="font-medium">{etiquetaMotivo(selected)}</span></div>
          <div><span className="text-slate-500">OC #:</span><span className="font-medium">{idsOC(selected).join(', ')||'—'}</span></div>
          <div><span className="text-slate-500">Fecha:</span><span className="font-medium">{selected.fecha?new Date(selected.fecha).toLocaleDateString('es-AR'):'—'}</span></div>
          <div><span className="text-slate-500">Ent. Acordada:</span><span className="font-medium">{selected.fechaEntregaAcordada?new Date(selected.fechaEntregaAcordada).toLocaleDateString('es-AR'):'—'}</span></div>
          <div><span className="text-slate-500">Estado:</span><span className="font-medium">{etiquetaEstado(selected)}</span></div>
          <div><span className="text-slate-500">Stock:</span><span className="font-medium">{estadoStock(selected)}</span></div>
          {selected.wmsStatus === 'error' && (
            <div className="sm:col-span-2 md:col-span-3 rounded-lg bg-red-50 px-3 py-2 text-red-800">
              {selected.wmsError?.trim() && <p className="font-medium">Motivo: {motivoError(selected.wmsError)}</p>}
              {user?.role === 'admin'
                ? <Link to="/mantenimiento" className="text-red-800 underline underline-offset-2 hover:text-red-900">Mantenimiento → Reintentar ingreso</Link>
                : <p>Avisale a un administrador para que lo revise.</p>}
            </div>
          )}
          {selected.creadoPor && <div><span className="text-slate-500">Cargado por:</span><span className="font-medium">{selected.creadoPor}{selected.createdAt ? ` · ${fechaHora(selected.createdAt)}` : ''}</span></div>}
          {selected.confirmadoPor && <div><span className="text-slate-500">Confirmado por:</span><span className="font-medium">{selected.confirmadoPor}{selected.confirmadoEn ? ` · ${fechaHora(selected.confirmadoEn)}` : ''}</span></div>}
          <div className="col-span-2"><span className="text-slate-500">Notas:</span><span className="font-medium">{selected.notas||'—'}</span></div>
        </div>
        {selected.items&&selected.items.length>0&&(()=>{
          // Las filas de una misma línea de OC (p. ej. repartida en variantes) cuentan juntas: lo recibido es la suma y el
          // pendiente se marca en la última. Un ítem sin línea es su propio grupo.
          const grupo = (it: { ordenCompraItemId?: number | null }, i: number) => (it.ordenCompraItemId != null ? `l${it.ordenCompraItemId}` : `i${i}`);
          const lineas = new Map<string, { descripcion: string; rec: number; ord: number; ultima: number }>();
          selected.items.forEach((it, i) => {
            const rec = parseFloat(it.cantidad || '0'); const previa = lineas.get(grupo(it, i));
            if (previa) { previa.rec += rec; previa.ultima = i; }
            else lineas.set(grupo(it, i), { descripcion: it.descripcion, rec, ord: parseFloat(it.cantidadOrdenada || '0'), ultima: i });
          });
          const esParcialLinea = (l: { rec: number; ord: number }) => l.ord > 0 && l.rec < l.ord;
          const itemsParciales = [...lineas.values()].filter(esParcialLinea);
          return (<>
            <h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Items ({selected.items.length})</h3>
            {itemsParciales.length>0&&(
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                <p className="font-semibold mb-1">⚠ Recepción parcial — {itemsParciales.length} ítem{itemsParciales.length>1?'s':''} con cantidad menor a la pedida:</p>
                <ul className="space-y-0.5 text-xs">
                  {itemsParciales.map(({ descripcion, rec, ord }, i)=>(
                    <li key={i}>· <strong>{descripcion}</strong> — recibido {rec} de {ord} ({ord-rec} pendiente{ord-rec!==1?'s':''})</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="table-wrap overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">SKU</th><th className="text-left px-2 py-1">Descripcion</th><th className="text-center px-2 py-1">UM</th>{idsOC(selected).length > 1 && <th className="text-left px-2 py-1">OC</th>}<th className="text-right px-2 py-1">Cant.OC</th><th className="text-right px-2 py-1">Cant.Recibida</th><th className="text-left px-2 py-1">Diferencia</th></tr></thead>
            <tbody>{selected.items.map((item,i)=>{
              const linea=lineas.get(grupo(item,i))!; const esParcial=esParcialLinea(linea);
              const pendiente=esParcial&&linea.ultima===i?linea.ord-linea.rec:null;
              return (<tr key={i} className={`border-t border-slate-50 ${esParcial?'bg-amber-50/50':''}`}>
                <td className="px-2 py-1 font-medium">{(item as any).sku?<CodigoArticulo sku={(item as any).sku} />:'—'}</td><td className="px-2 py-1">{item.descripcion}{item.varianteNombre&&<span className="ml-1 text-xs text-slate-500">· {item.varianteNombre}</span>}</td><td className="text-center px-2 py-1 text-slate-400">{(item as any).unidadMedida||'UN'}</td>
                {idsOC(selected).length > 1 && <td className="px-2 py-1 text-slate-600">{item.ordenCompraId ? `#${item.ordenCompraId}` : '—'}</td>}
                <td className="text-right px-2 py-1 text-slate-400">{(item as any).cantidadOrdenada||'—'}</td>
                <td className={`text-right px-2 py-1 font-medium ${esParcial?'text-amber-700':''}`}>{item.cantidad}{pendiente!=null&&<span className="ml-1 text-[10px] text-amber-600">({pendiente} pend.)</span>}</td>
                <td className="px-2 py-1 text-xs text-slate-600">{item.diferenciaDetalle||'—'}</td>
              </tr>);
            })}</tbody></table></div>
          </>);
        })()}
        <div className="sticky bottom-0 -mx-6 px-6 bg-white pt-3 pb-1 border-t border-slate-200 flex flex-wrap justify-between gap-2">
          {selected.estado === 'pendiente' ? (
            <div className="flex flex-wrap items-center gap-2">
              {selected.borrador && (can('remitos', 'eliminar') || (can('remitos', 'crear') && user?.id != null && user.id === selected.creadoPorId)) && <Button variant="ghost" onClick={() => seguirCargando(selected)}>Seguir cargando</Button>}
              {puedeConfirmar
                ? <Button onClick={() => setConfirmando(true)} title="Revisá lo cargado: al confirmarlo suma al stock y pasa a Facturas">Confirmar</Button>
                : <p className="px-2 py-2 text-sm text-slate-500">{selected.borrador ? 'Borrador: todavía no suma al stock.' : 'Pendiente.'} Lo confirma un administrador.</p>}
              {selected.borrador
                ? (can('remitos', 'eliminar') || (can('remitos', 'crear') && user?.id != null && user.id === selected.creadoPorId)) && (
                  <button onClick={() => setBorrandoBorrador(true)} className="px-4 py-2 text-sm text-red-600 hover:text-red-800">Eliminar borrador</button>
                )
                : can('remitos', 'eliminar') && (
                  <button onClick={() => setConfirmDelete(true)} className="px-4 py-2 text-sm text-red-600 hover:text-red-800">Eliminar</button>
                )}
            </div>
          ) : !can('remitos', 'eliminar') ? (
            <p className="px-4 py-2 text-sm text-slate-500">Remito confirmado: no se puede cambiar ni borrar.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setConfirmDelete(true)} title={`El remito está confirmado. ${avisoEliminar(selected)}`} className="px-4 py-2 text-sm text-amber-600 hover:text-amber-800">⚠ Eliminar (confirmado)</button>
              {forzable?.id === selected.id && (
                <button onClick={() => setConfirmForzado(true)} title="Sólo administradores: lo borra aunque su stock ya no esté (puede quedar negativo)" className="px-4 py-2 text-sm font-medium text-red-700 hover:text-red-900">Eliminar igual</button>
              )}
            </div>
          )}
          <button onClick={()=>setDetailOpen(false)} className="px-4 py-2 text-sm">Cerrar</button>
        </div></div>)}
      </Modal>
      <PendientesDeRecibir open={pendientesOpen} onClose={cerrarPendientes} onRecibir={can('remitos', 'crear') ? recibirOC : undefined} recibiendo={recibiendo} />
      <Confirm open={confirmDelete} title="Eliminar Remito" message={selected ? `${selected.estado === 'conciliado' ? 'Remito confirmado, ¿seguro que desea eliminar?' : `¿Estás seguro de eliminar el remito #${selected.id}?`} ${avisoEliminar(selected)}` : ''} confirmLabel="Eliminar" danger onConfirm={handleDeleteRemito} onCancel={() => setConfirmDelete(false)} />
      <Confirm open={confirmando} title="Confirmar remito"
        message={selected ? `Remito ${nombreRemito(selected)} de ${contraparte(selected)}${selected.creadoPor ? `, cargado por ${selected.creadoPor}` : ''}.\n${resumenItems(selected)}.\n\nAl confirmarlo, la mercadería suma al stock${idsOC(selected).length ? ', cuenta como recibida en la OC' : ''} y el remito pasa a Facturas. Revisá que lo cargado coincida con lo que llegó.` : ''}
        confirmLabel="Confirmar" onConfirm={handleConfirmar} onCancel={() => setConfirmando(false)} />
      <Confirm open={borrandoBorrador} title="Eliminar borrador" message={selected ? `¿Eliminar el borrador ${nombreRemito(selected)}? No movió stock: se borra sin tocar nada.` : ''} confirmLabel="Eliminar" danger onConfirm={handleEliminarBorrador} onCancel={() => setBorrandoBorrador(false)} />
      <Confirm open={confirmForzado} title="Eliminar igual"
        message={forzable ? `${forzable.mensaje}\n\nSi lo eliminás igual, se descuenta lo que haya en el depósito (una ubicación nunca queda negativa; lo que falta queda anotado) y el stock disponible para vender de esos SKU puede quedar negativo. Usalo sólo para remitos cargados por error (por ejemplo, de prueba).` : ''}
        confirmLabel="Eliminar igual" danger onConfirm={handleEliminarForzado} onCancel={() => setConfirmForzado(false)} />
      <Confirm open={importItemsDraft !== null} title="Items detectados en PDF" message={`Se detectaron ${importItemsDraft?.length || 0} items. ¿Cargar con items?`} confirmLabel="Cargar items" disabled={verificandoPdf} onConfirm={confirmImportItems} onCancel={() => setImportItemsDraft(null)} />
    </div>
  );
}
